import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/data';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-file-unknow-line text-2xl text-foreground-300" />
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <article className="bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: article.categoryName, path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <header className="mt-8">
              <Link
                to={`/category/${article.category}`}
                className="inline-block text-xs font-medium text-primary-600 bg-primary-100/80 px-2.5 py-1 rounded-md mb-4 hover:bg-primary-200 transition-colors cursor-pointer"
              >
                {article.categoryName}
              </Link>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight">
                {article.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-400">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line text-xs w-3 h-3 flex items-center justify-center" />
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line text-xs w-3 h-3 flex items-center justify-center" />
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line text-xs w-3 h-3 flex items-center justify-center" />
                  阅读 {article.readTime}
                </span>
              </div>
            </header>

            <div className="mt-8 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover"
              />
            </div>

            <div
              className="mt-10 prose max-w-none"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-foreground-500">标签：</span>
                <span className="px-3 py-1 bg-background-100 text-xs text-foreground-600 rounded-full">91暗</span>
                <span className="px-3 py-1 bg-background-100 text-xs text-foreground-600 rounded-full">{article.categoryName}</span>
                <span className="px-3 py-1 bg-background-100 text-xs text-foreground-600 rounded-full">91暗指南</span>
              </div>
            </div>
          </div>

          <style>{`
            .prose h2 { font-family: var(--font-heading); font-size: 1.5rem; font-weight: 700; color: oklch(var(--foreground-900)); margin-top: 2rem; margin-bottom: 1rem; }
            .prose h3 { font-family: var(--font-heading); font-size: 1.25rem; font-weight: 600; color: oklch(var(--foreground-800)); margin-top: 1.5rem; margin-bottom: 0.75rem; }
            .prose p { font-size: 1rem; line-height: 1.85; color: oklch(var(--foreground-700)); margin-bottom: 1.25rem; }
            .prose strong { color: oklch(var(--foreground-900)); font-weight: 600; }
            .prose ul { margin-bottom: 1.25rem; padding-left: 1.5rem; }
            .prose li { font-size: 1rem; line-height: 1.85; color: oklch(var(--foreground-700)); margin-bottom: 0.5rem; list-style-type: disc; }
          `}</style>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: article.title,
                description: article.excerpt,
                image: article.image,
                author: { '@type': 'Organization', name: article.author },
                datePublished: article.date,
                publisher: {
                  '@type': 'Organization',
                  name: '91暗指南',
                },
                mainEntityOfPage: {
                  '@type': 'WebPage',
                  '@id': `https://zxymg.cn/article/${article.slug}`,
                },
              }),
            }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: '首页', item: 'https://zxymg.cn/' },
                  { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://zxymg.cn/category/${article.category}` },
                  { '@type': 'ListItem', position: 3, name: article.title },
                ],
              }),
            }}
          />
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} {...ra} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}